/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useStatus } from '@/hooks/use-status'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { validateRegisterCode } from '@/features/auth/api'
import { OAuthProviders } from '@/features/auth/components/oauth-providers'

export function SignUpForm({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation()
  const [registerCode, setRegisterCode] = useState('')
  const [codeValidated, setCodeValidated] = useState(false)
  const [isValidatingCode, setIsValidatingCode] = useState(false)

  const { status } = useStatus()

  const scheduledRegisterEnabled = Boolean(
    status?.scheduled_register_enabled ?? status?.data?.scheduled_register_enabled
  )
  const registerWeekday = status?.register_weekday ?? status?.data?.register_weekday ?? 6
  const registerStartHour = status?.register_start_hour ?? status?.data?.register_start_hour ?? 14
  const registerEndHour = status?.register_end_hour ?? status?.data?.register_end_hour ?? 15
  const registerSlotLimit = status?.register_slot_limit ?? status?.data?.register_slot_limit ?? 100
  const registerSlotRemaining = status?.register_slot_remaining ?? status?.data?.register_slot_remaining ?? -1
  const registerCodeEnabled = Boolean(
    status?.register_code_enabled ?? status?.data?.register_code_enabled
  )

  const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const weekdayName = weekdayNames[registerWeekday] ?? 'Saturday'

  const [now, setNow] = useState(() => Date.now())
  const tickRef = useRef(null)

  useEffect(() => {
    if (!scheduledRegisterEnabled) return
    tickRef.current = setInterval(() => {
      setNow(Date.now())
    }, 60_000)
    return () => {
      if (tickRef.current) clearInterval(tickRef.current)
    }
  }, [scheduledRegisterEnabled])

  const isRegisterOpen = useMemo(() => {
    if (!scheduledRegisterEnabled) return true
    const d = new Date(now)
    const day = d.getDay()
    const hour = d.getHours()
    if (day !== registerWeekday) return false
    if (hour < registerStartHour || hour >= registerEndHour) return false
    // Check if slots are available
    if (registerSlotRemaining === 0) return false
    return true
  }, [scheduledRegisterEnabled, registerWeekday, registerStartHour, registerEndHour, registerSlotRemaining, now])

  const isSlotFull = scheduledRegisterEnabled && registerSlotRemaining === 0
  const showNotice = scheduledRegisterEnabled && (!isRegisterOpen || isSlotFull)
  const canRegister =
    (!scheduledRegisterEnabled || isRegisterOpen || codeValidated) &&
    (!registerCodeEnabled || codeValidated)

  async function handleValidateCode() {
    if (!registerCode.trim()) {
      toast.error(t('Please enter a registration code'))
      return
    }
    setIsValidatingCode(true)
    try {
      const res = await validateRegisterCode(registerCode.trim())
      if (res?.success) {
        setCodeValidated(true)
        toast.success(t('Registration code verified'))
      } else {
        toast.error(res?.message || t('Invalid registration code'))
      }
    } catch (_error) {
      toast.error(t('Invalid registration code'))
    } finally {
      setIsValidatingCode(false)
    }
  }

  return (
    <div className={cn('grid gap-4', className)} {...props}>
      {showNotice && (
        <Alert variant='default' className='mt-2'>
          <AlertTriangle className='h-4 w-4' />
          <AlertDescription>
            {isSlotFull
              ? t(
                  'Registration slots are full for this session ({{limit}}). Please use a registration code to register.',
                  { limit: registerSlotLimit }
                )
              : t(
                  'Registration is open every {{weekday}} {{startHour}}:00~{{endHour}}:00, limited to {{limit}} registrations per session',
                  { weekday: t(weekdayName), startHour: registerStartHour, endHour: registerEndHour, limit: registerSlotLimit }
                )}
          </AlertDescription>
        </Alert>
      )}

      {registerCodeEnabled && !codeValidated && (
        <div className='grid gap-2'>
          <Label>{t('Registration code')}</Label>
          <div className='flex gap-2'>
            <Input
              placeholder={t('Enter registration code')}
              value={registerCode}
              onChange={(e) => setRegisterCode(e.target.value)}
              className='flex-1'
            />
            <Button
              type='button'
              onClick={handleValidateCode}
              disabled={isValidatingCode || !registerCode.trim()}
            >
              {isValidatingCode ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : null}
              {t('Verify')}
            </Button>
          </div>
        </div>
      )}

      {codeValidated && (
        <Alert variant='default' className='border-green-500'>
          <CheckCircle2 className='h-4 w-4 text-green-500' />
          <AlertDescription className='text-green-600'>
            {t('Registration code verified. You can now register.')}
          </AlertDescription>
        </Alert>
      )}

      {canRegister && (
        <OAuthProviders
          status={status}
          registerCode={registerCode}
          className='pt-2'
        />
      )}
    </div>
  )
}
